import { environment } from '../config';
import React, { useEffect, useState, useCallback } from 'react';
import TvRefreshNotice, { TvRefreshProblem } from '../components/TvRefreshNotice';
import { GOV_EVENT_KINDS } from '../components/calendarGovStyles';

const params = new URLSearchParams(window.location.search);
const TV_TOKEN = params.get('token') || '';
const SHOW_PARAM = params.get('show') || 'jobs,events,calendars,gov';
const JOB_TYPES_PARAM = params.get('jobTypes') || '';
const CALENDARS_PARAM = params.get('calendars') || '';

const API_BASE = environment.apiUrl;

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

const JOB_COLORS: Record<string, { bg: string; label: string; icon: string }> = {
  roofing:   { bg: '#4A7EB5', label: 'Roofing',   icon: '🏠' },
  insurance: { bg: '#06B6D4', label: 'Insurance', icon: '🛡️' },
  asphalt:   { bg: '#F59E0B', label: 'Asphalt',   icon: '🛣️' },
  concrete:  { bg: '#8B5CF6', label: 'Concrete',  icon: '🪨' },
};

const EVENT_COLORS: Record<string, { bg: string; label: string; icon: string }> = {
  meeting:   { bg: '#3B82F6', label: 'Meeting', icon: '👥' },
  interview: { bg: '#8B5CF6', label: 'Interview', icon: '🎤' },
  follow_up: { bg: '#F97316', label: 'Follow Up', icon: '📞' },
  reminder:  { bg: '#EAB308', label: 'Reminder', icon: '⏰' },
  training:  { bg: '#22C55E', label: 'Training', icon: '📚' },
  other:     { bg: '#6B7280', label: 'Other', icon: '📅' },
};

interface Job {
  id: string; job_number: string; homeowner_name: string; address: string;
  job_type: string; scheduled_date: string; scheduled_time: string | null;
  pipeline_stage_name: string; leads?: { homeowner_name: string; address: string };
}

interface CrmEvent {
  id: string; title: string; description?: string; event_type: string;
  event_date: string; event_time?: string | null; location?: string; all_day?: boolean;
  related_entity_type?: string; event_kind?: string; color?: string;
}

interface GovEvent {
  id: string; title: string; description?: string; event_kind: string;
  event_date: string; event_time?: string | null;
}

interface CalendarEvent {
  id: string; title: string; description?: string; location?: string;
  event_date: string; event_time?: string; all_day?: boolean;
  calendar_id: string; calendar_name: string; calendar_color: string; calendar_icon: string;
}

interface TeamCalendar {
  calendar_id: string; display_name: string; color: string; icon: string;
}

// Unified item for display
interface CalendarItem {
  id: string;
  type: 'job' | 'event' | 'calendar';
  title: string;
  subtitle?: string;
  date: string;
  time?: string;
  color: string;
  icon: string;
  label: string;
}

function useWakeLock() {
  useEffect(() => {
    let wakeLock: any = null;
    const acquire = async () => {
      try {
        if ('wakeLock' in navigator) {
          wakeLock = await (navigator as any).wakeLock.request('screen');
        }
      } catch (_) {}
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      wakeLock?.release();
    };
  }, []);
}

function useClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setTime(new Date()), 1000); return () => clearInterval(t); }, []);
  return time;
}

function fmtTime(t: string | null | undefined) {
  if (!t) return '';
  const m = t.match(/^(\d{2}):(\d{2})/);
  if (!m) return '';
  const h = parseInt(m[1]);
  return `${(h % 12) || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
}

function useTvScale() {
  const [scale, setScale] = React.useState(1);
  React.useEffect(() => {
    const calc = () => {
      const sx = window.innerWidth / 1920;
      const sy = window.innerHeight / 1080;
      setScale(Math.min(sx, sy));
    };
    calc();
    window.addEventListener('resize', calc);
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => window.removeEventListener('resize', calc);
  }, []);
  return scale;
}

// Parse show param
const showSet = new Set(SHOW_PARAM.split(',').map(s => s.trim().toLowerCase()));
const showJobs = showSet.has('jobs');
const showEvents = showSet.has('events');
const showCalendars = showSet.has('calendars');
const showGov = showSet.has('gov');

export default function TvCalendarPage() {
  const scale = useTvScale();
  const [valid, setValid] = useState<boolean | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [events, setEvents] = useState<CrmEvent[]>([]);
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [teamCalendars, setTeamCalendars] = useState<TeamCalendar[]>([]);
  const [govSolicitations, setGovSolicitations] = useState<GovEvent[]>([]);
  const [govConstruction, setGovConstruction] = useState<GovEvent[]>([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [refreshProblem, setRefreshProblem] = useState<TvRefreshProblem>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  useWakeLock();
  const now = useClock();

  const enterFullscreen = () => {
    const el = document.documentElement as any;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    else if (el.mozRequestFullScreen) el.mozRequestFullScreen();
    else if (el.msRequestFullscreen) el.msRequestFullscreen();
  };

  useEffect(() => {
    const onFsChange = () => {
      const fs = !!(document as any).fullscreenElement || !!(document as any).webkitFullscreenElement;
      setIsFullscreen(fs);
      if (!fs) setTimeout(enterFullscreen, 500);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
    };
  }, []);

  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const year = today.getFullYear();
  const month = today.getMonth();

  const fetchData = useCallback(async () => {
    try {
      let url = `${API_BASE}/tv/calendar?token=${encodeURIComponent(TV_TOKEN)}&show=${encodeURIComponent(SHOW_PARAM)}`;
      if (JOB_TYPES_PARAM) url += `&jobTypes=${encodeURIComponent(JOB_TYPES_PARAM)}`;
      if (CALENDARS_PARAM) url += `&calendars=${encodeURIComponent(CALENDARS_PARAM)}`;
      
      const r = await fetch(url, { cache: 'no-store' });
      if (!r.ok) {
        setRefreshProblem(r.status === 401 || r.status === 403 ? 'access' : 'connection');
        return;
      }
      const data = await r.json();
      
      // Handle both old (array) and new (object) response formats
      if (Array.isArray(data)) {
        setJobs(data);
      } else {
        setJobs(data.jobs || []);
        setEvents(data.events || []);
        setCalendarEvents(data.calendarEvents || []);
        setTeamCalendars(data.teamCalendars || []);
        setGovSolicitations(data.govSolicitations || []);
        setGovConstruction(data.govConstruction || []);
      }
      setLastUpdated(new Date());
      setRefreshProblem(null);
      window.dispatchEvent(new Event('resize'));
    } catch { setRefreshProblem('connection'); }
  }, []);

  useEffect(() => {
    fetch(`${API_BASE}/tv/verify?token=${encodeURIComponent(TV_TOKEN)}`, { cache: 'no-store' })
      .then(r => setValid(r.ok)).catch(() => setValid(false));
  }, []);

  useEffect(() => {
    if (!valid) return;
    fetchData();
    const t = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [valid, fetchData]);

  // Build calendar grid
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const daysInPrev = new Date(year, month, 0).getDate();
  const cells: { date: Date; current: boolean }[] = [];
  for (let i = firstDay - 1; i >= 0; i--) cells.push({ date: new Date(year, month - 1, daysInPrev - i), current: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ date: new Date(year, month, d), current: true });
  while (cells.length < 42) cells.push({ date: new Date(year, month + 1, cells.length - daysInMonth - firstDay + 1), current: false });

  // Convert all data to unified CalendarItems
  const getItemsForDate = (date: Date): CalendarItem[] => {
    const ds = date.toISOString().split('T')[0];
    const items: CalendarItem[] = [];

    // Jobs
    jobs.filter(j => j.scheduled_date === ds).forEach(j => {
      const jc = JOB_COLORS[j.job_type] || JOB_COLORS.roofing;
      items.push({
        id: `job-${j.id}`,
        type: 'job',
        title: j.leads?.homeowner_name || j.homeowner_name || 'Job',
        subtitle: j.leads?.address || j.address,
        date: ds,
        time: j.scheduled_time || undefined,
        color: jc.bg,
        icon: jc.icon,
        label: jc.label,
      });
    });

    // CRM Events
    events.filter(e => e.event_date === ds).forEach(e => {
      const ec = EVENT_COLORS[e.event_type] || EVENT_COLORS.other;
      const gov = e.related_entity_type?.startsWith('gov_') && e.event_kind
        ? GOV_EVENT_KINDS[e.event_kind] : null;
      items.push({
        id: `event-${e.id}`,
        type: 'event',
        title: e.title,
        subtitle: e.location || e.description,
        date: ds,
        time: e.event_time || undefined,
        color: e.color || gov?.color || ec.bg,
        icon: gov?.icon || ec.icon,
        label: gov?.label || ec.label,
      });
    });

    // Raw government dates that have not been scheduled as CRM events.
    [...govSolicitations, ...govConstruction].filter(e => e.event_date === ds).forEach(e => {
      const gov = GOV_EVENT_KINDS[e.event_kind] || GOV_EVENT_KINDS.other;
      items.push({
        id: e.id,
        type: 'event',
        title: e.title,
        subtitle: e.description,
        date: ds,
        time: e.event_time || undefined,
        color: gov.color,
        icon: gov.icon,
        label: gov.label,
      });
    });

    // Google Calendar Events
    calendarEvents.filter(e => e.event_date === ds).forEach(e => {
      items.push({
        id: `cal-${e.id}`,
        type: 'calendar',
        title: e.title,
        subtitle: e.location,
        date: ds,
        time: e.event_time,
        color: e.calendar_color,
        icon: e.calendar_icon,
        label: e.calendar_name,
      });
    });

    // Sort by time
    return items.sort((a, b) => {
      if (!a.time && !b.time) return 0;
      if (!a.time) return 1;
      if (!b.time) return -1;
      return a.time.localeCompare(b.time);
    });
  };

  const isToday = (date: Date) => date.toDateString() === today.toDateString();

  const todayStr = today.toISOString().split('T')[0];
  const todayItems = getItemsForDate(today);

  // Upcoming: next 7 days
  const upcoming: { label: string; items: CalendarItem[] }[] = [];
  for (let i = 1; i <= 7; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    const dayItems = getItemsForDate(d);
    if (dayItems.length > 0) {
      upcoming.push({ label: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }), items: dayItems });
    }
  }

  const timeStr = now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  const dateStr = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });

  // Build legend based on what's shown
  const legendItems: { bg: string; label: string }[] = [];
  if (showJobs) {
    Object.entries(JOB_COLORS).forEach(([_, { bg, label, icon }]) => {
      legendItems.push({ bg, label: `${icon} ${label}` });
    });
  }
  if (showEvents) {
    legendItems.push({ bg: '#3B82F6', label: '📅 Events' });
  }
  if (showGov) {
    legendItems.push({ bg: '#DC2626', label: '🚨 Gov deadlines' });
  }
  if (showCalendars && teamCalendars.length > 0) {
    teamCalendars.forEach(tc => {
      legendItems.push({ bg: tc.color, label: `${tc.icon} ${tc.display_name}` });
    });
  }

  // Count items for header
  const monthItems = [...jobs, ...events, ...calendarEvents, ...govSolicitations, ...govConstruction].filter(item => {
    const dateStr = 'scheduled_date' in item ? item.scheduled_date : item.event_date;
    const d = new Date(dateStr);
    return d.getMonth() === month && d.getFullYear() === year;
  });

  if (valid === null) return (
    <div style={{ background: '#0F1923', width: '100vw', height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ color: '#4A7EB5', fontSize: 24 }}>Loading...</div>
    </div>
  );
  if (!valid) return (
    <div style={{ background: '#0F1923', width: '100vw', height: '100vh', position: 'relative' }}>
      <TvRefreshNotice problem="access" lastUpdated={null} />
    </div>
  );

  return (
    <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', background: '#0F1923' }} onClick={!isFullscreen ? enterFullscreen : undefined}>
      {!isFullscreen && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(15,25,35,0.85)', cursor: 'pointer' }}>
          <div style={{ color: '#fff', fontSize: 32, fontWeight: 700, textAlign: 'center', pointerEvents: 'none' }}>
            <div style={{ fontSize: 64, marginBottom: 16 }}>⛶</div>
            Tap anywhere to go fullscreen
          </div>
        </div>
      )}
      <div style={{ width: 1920, height: 1080, transform: `scale(${scale})`, transformOrigin: 'top left', display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif', background: '#0F1923' }}>

        <TvRefreshNotice problem={refreshProblem} lastUpdated={lastUpdated} />

        {/* Header */}
        <div style={{ background: '#1A2B3C', borderBottom: '2px solid #4A7EB5', padding: '10px 32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <img src="/logo-white.png" alt="McMullen Browne" style={{ height: 44, width: 'auto', objectFit: 'contain', maxWidth: 220 }} />
            <div style={{ background: '#4A7EB522', border: '1px solid #4A7EB544', borderRadius: 8, padding: '4px 14px', fontSize: 13, fontWeight: 700, color: '#4A7EB5', textTransform: 'uppercase' as const, letterSpacing: 1 }}>📅 Schedule</div>
            {/* Legend */}
            <div style={{ display: 'flex', gap: 6, marginLeft: 12, flexWrap: 'wrap' }}>
              {legendItems.map(({ bg, label }, i) => (
                <div key={i} style={{ background: bg + '22', border: `1px solid ${bg}55`, borderRadius: 12, padding: '2px 8px', fontSize: 11, fontWeight: 700, color: bg }}>{label}</div>
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 32, fontWeight: 800, color: '#fff', lineHeight: 1 }}>{timeStr}</div>
            <div style={{ fontSize: 13, color: '#8FA8C0', marginTop: 2 }}>{dateStr}</div>
          </div>
        </div>

        {/* Body — 2 columns */}
        <div style={{ flex: 1, display: 'grid', gridTemplateColumns: '1fr 420px', gap: 0, overflow: 'hidden' }}>

          {/* Left: Calendar */}
          <div style={{ padding: '8px 12px 8px 16px', display: 'flex', flexDirection: 'column' }}>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', marginBottom: 6 }}>
              {MONTHS[month]} {year}
              <span style={{ color: '#4A6580', fontSize: 14, fontWeight: 400, marginLeft: 12 }}>{monthItems.length} items this month</span>
            </div>

            {/* Day headers */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 3, marginBottom: 2, flexShrink: 0 }}>
              {DAYS.map(d => <div key={d} style={{ textAlign: 'center', color: '#4A6580', fontSize: 11, fontWeight: 700, padding: '2px 0' }}>{d}</div>)}
            </div>

            {/* Calendar grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gridTemplateRows: 'repeat(6, 1fr)', gap: 3, flex: 1, minHeight: 0 }}>
              {cells.map((cell, i) => {
                const cellItems = getItemsForDate(cell.date);
                const isTodayCell = isToday(cell.date);
                return (
                  <div key={i} style={{
                    background: isTodayCell ? '#1E3A5F' : cell.current ? '#1A2B3C' : '#111B26',
                    borderRadius: 6,
                    padding: '4px 6px',
                    border: isTodayCell ? '2px solid #4A7EB5' : '1px solid #2D4259',
                    display: 'flex', flexDirection: 'column', gap: 2,
                    overflow: 'hidden',
                    minHeight: 0,
                  }}>
                    <div style={{ fontSize: 13, fontWeight: isTodayCell ? 900 : 600, color: isTodayCell ? '#4A7EB5' : cell.current ? '#fff' : '#2D4259', marginBottom: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
                      {cell.date.getDate()}
                      {isTodayCell && <span style={{ fontSize: 8, color: '#4A7EB5', fontWeight: 700, background: '#4A7EB522', borderRadius: 3, padding: '1px 3px' }}>TODAY</span>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2, overflow: 'hidden', flex: 1, minHeight: 0 }}>
                      {cellItems.slice(0, 3).map(item => (
                        <div key={item.id} style={{ background: item.color + '33', border: `1px solid ${item.color}66`, borderRadius: 3, padding: '2px 4px', overflow: 'hidden' }}>
                          <div style={{ fontSize: 10, color: '#fff', fontWeight: 700, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</div>
                          {item.time && <div style={{ fontSize: 9, color: item.color, fontWeight: 600, lineHeight: 1.1 }}>{fmtTime(item.time)}</div>}
                        </div>
                      ))}
                      {cellItems.length > 3 && (
                        <div style={{ fontSize: 9, color: '#8FA8C0', fontWeight: 600, paddingLeft: 2 }}>+{cellItems.length - 3} more</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Today + upcoming */}
          <div style={{ background: '#1A2B3C', borderLeft: '1px solid #2D4259', padding: '20px 20px', display: 'flex', flexDirection: 'column', gap: 0, overflowY: 'auto' }}>

            {/* Today */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 12 }}>
                Today <span style={{ color: '#4A7EB5', marginLeft: 4 }}>{todayItems.length}</span>
              </div>
              {todayItems.length === 0 ? (
                <div style={{ color: '#4A6580', fontSize: 16, textAlign: 'center', padding: '20px 0' }}>Nothing scheduled</div>
              ) : (
                todayItems.map(item => (
                  <div key={item.id} style={{ background: '#243447', borderRadius: 10, padding: '12px 14px', marginBottom: 8, borderLeft: `4px solid ${item.color}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                      <div style={{ color: '#fff', fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{item.title}</div>
                      {item.time && <div style={{ color: '#4A7EB5', fontSize: 15, fontWeight: 700, flexShrink: 0 }}>{fmtTime(item.time)}</div>}
                    </div>
                    {item.subtitle && <div style={{ color: '#8FA8C0', fontSize: 14, marginBottom: 6, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {item.subtitle}</div>}
                    <div style={{ display: 'inline-block', background: item.color + '22', border: `1px solid ${item.color}55`, borderRadius: 20, padding: '3px 12px', fontSize: 13, fontWeight: 700, color: item.color }}>{item.icon} {item.label}</div>
                  </div>
                ))
              )}
            </div>

            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div>
                <div style={{ height: 1, background: '#2D4259', marginBottom: 12 }} />
                <div style={{ fontSize: 16, fontWeight: 700, color: '#4A6580', textTransform: 'uppercase' as const, letterSpacing: 1, marginBottom: 10 }}>Upcoming</div>
                {upcoming.map(({ label, items }) => (
                  <div key={label} style={{ marginBottom: 12 }}>
                    <div style={{ color: '#4A7EB5', fontSize: 14, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 6 }}>{label}</div>
                    {items.map(item => (
                      <div key={item.id} style={{ background: '#243447', borderRadius: 8, padding: '9px 12px', marginBottom: 6, borderLeft: `4px solid ${item.color}` }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 6, marginBottom: 3 }}>
                          <div style={{ color: '#fff', fontSize: 15, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{item.title}</div>
                          {item.time && <div style={{ color: '#4A7EB5', fontSize: 13, fontWeight: 700, flexShrink: 0 }}>{fmtTime(item.time)}</div>}
                        </div>
                        {item.subtitle && <div style={{ color: '#8FA8C0', fontSize: 12, marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>📍 {item.subtitle}</div>}
                        <div style={{ display: 'inline-block', background: item.color + '22', border: `1px solid ${item.color}55`, borderRadius: 20, padding: '2px 10px', fontSize: 12, fontWeight: 700, color: item.color }}>{item.icon} {item.label}</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
